import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, CalendarCheck, HandshakeIcon, Target, CheckCircle2, CalendarDays } from 'lucide-react';
type Goal = {
  id: string;
  title: string;
  current: number;
  target: number;
  icon: string;
};
const INITIAL_GOALS: Goal[] = [{
  id: '1',
  title: 'Get 100 dials',
  current: 100,
  target: 100,
  icon: 'phone'
}, {
  id: '2',
  title: 'Set 15 appointments',
  current: 15,
  target: 15,
  icon: 'calendar'
}, {
  id: '3',
  title: 'Close 5 loans this month',
  current: 3,
  target: 5,
  icon: 'handshake'
}];
export function GoalTracker({
  onNavigate
}: {
  onNavigate?: (view: 'monthly') => void;
}) {
  const [goals, setGoals] = useState<Goal[]>(INITIAL_GOALS);
  const [newTitle, setNewTitle] = useState('');
  const [newTarget, setNewTarget] = useState('');
  const handleAddGoal = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    const targetValue = parseInt(newTarget);
    const finalTarget = isNaN(targetValue) || targetValue <= 0 ? 10 : targetValue;
    const newGoal: Goal = {
      id: crypto.randomUUID(),
      title: newTitle.trim(),
      current: 0,
      target: finalTarget
    };
    setGoals([newGoal, ...goals]);
    setNewTitle('');
    setNewTarget('');
  };
  const handleIncrement = (id: string) => {
    setGoals(goals.map(goal => {
      if (goal.id === id && goal.current < goal.target) {
        return {
          ...goal,
          current: goal.current + 1
        };
      }
      return goal;
    }));
  };
  const handleDelete = (id: string) => {
    setGoals(goals.filter(goal => goal.id !== id));
  };
  // Calculate overall progress
  const totalGoals = goals.length;
  const completedGoals = goals.filter(g => g.current >= g.target).length;
  return <div className="w-full max-w-2xl mx-auto p-6 pt-12 md:pt-20" data-id="element-3">
      {/* Header */}
      <div className="mb-10" data-id="element-4">
        <div className="flex justify-between items-start mb-2" data-id="element-5">
          <h1 className="text-3xl font-bold text-[#1B6B3A] tracking-tight" data-id="element-6">
            Perfect Day
          </h1>
          {onNavigate && <button onClick={() => onNavigate('monthly')} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#1B6B3A] bg-[#1B6B3A]/10 hover:bg-[#1B6B3A]/20 rounded-lg transition-colors" data-id="element-7">
              <CalendarDays size={16} data-id="element-8" />
              Month Totals
            </button>}
        </div>
        <p className="text-gray-400 text-sm mb-2" data-id="element-9">
          {new Date().toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          year: 'numeric'
        })}
        </p>
        <p className="text-gray-500 text-lg" data-id="element-10">
          {totalGoals === 0 ? "You don't have any goals yet." : `${completedGoals} of ${totalGoals} goals completed`}
        </p>
        {totalGoals > 0 && <div className="mt-4" data-id="element-11">
            <div className="flex justify-between text-sm font-medium mb-2" data-id="element-12">
              <span className="text-gray-500" data-id="element-13">Overall Progress</span>
              <span className="text-[#1B6B3A]" data-id="element-14">
                {Math.round(completedGoals / totalGoals * 100)}%
              </span>
            </div>
            <div className="h-4 bg-white rounded-full overflow-hidden relative border border-gray-200/60" data-id="element-15">
              <motion.div className="absolute top-0 left-0 h-full rounded-full bg-[#1B6B3A]" initial={{
            width: 0
          }} animate={{
            width: `${completedGoals / totalGoals * 100}%`
          }} transition={{
            type: 'spring',
            bounce: 0,
            duration: 0.8
          }} data-id="element-16" />
            </div>
          </div>}
      </div>

      {/* Goals List */}
      <div className="space-y-4" data-id="element-17">
        <AnimatePresence mode="popLayout" data-id="element-18">
          {goals.map((goal, index) => {
          const isCompleted = goal.current >= goal.target;
          const progressPercentage = Math.min(100, goal.current / goal.target * 100);
          return <motion.div key={goal.id} layout initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95,
            transition: {
              duration: 0.2
            }
          }} transition={{
            layout: {
              type: 'spring',
              bounce: 0.3,
              duration: 0.6
            },
            opacity: {
              duration: 0.2
            }
          }} className={`group relative overflow-hidden bg-white rounded-2xl p-5 sm:p-6 shadow-sm border transition-colors duration-300 ${isCompleted ? 'border-[#1B6B3A]/30 bg-[#1B6B3A]/5' : 'border-gray-200/60'}`} data-id="element-19">
                <div className="flex items-center gap-3 mb-4" data-id="element-20">
                  <div className={`flex-shrink-0 p-2.5 rounded-xl ${isCompleted ? 'bg-[#1B6B3A]/10 text-[#1B6B3A]' : 'bg-gray-100 text-gray-500'}`} data-id="element-21">
                    {goal.icon === 'phone' && <Phone size={20} data-id="element-22" />}
                    {goal.icon === 'calendar' && <CalendarCheck size={20} data-id="element-23" />}
                    {goal.icon === 'handshake' && <HandshakeIcon size={20} data-id="element-24" />}
                  </div>
                  <h3 className={`font-semibold text-lg transition-colors duration-300 ${isCompleted ? 'text-gray-900' : 'text-gray-800'}`} data-id="element-25">
                    {goal.title}
                  </h3>
                </div>

                <div className="flex items-end gap-4 sm:gap-6" data-id="element-26">
                  <div className="flex-1" data-id="element-27">
                    <div className="flex justify-between text-sm font-medium mb-2.5" data-id="element-28">
                      <span className="text-gray-500 flex items-center gap-1.5" data-id="element-29">
                        {isCompleted && <CheckCircle2 size={16} className="text-[#1B6B3A]" data-id="element-30" />}
                        {isCompleted ? 'Completed' : 'Progress'}
                      </span>
                      <span className={isCompleted ? 'text-[#1B6B3A]' : 'text-gray-700'} data-id="element-31">
                        {goal.current}{' '}
                        <span className="text-gray-400 font-normal" data-id="element-32">
                          / {goal.target}
                        </span>
                      </span>
                    </div>

                    {/* Progress Bar */}
                    <div className="h-3 bg-gray-100 rounded-full overflow-hidden relative" data-id="element-33">
                      <motion.div className={`absolute top-0 left-0 h-full rounded-full ${isCompleted ? 'bg-[#1B6B3A]' : 'bg-[#1B6B3A]/80'}`} initial={{
                    width: 0
                  }} animate={{
                    width: `${progressPercentage}%`
                  }} transition={{
                    type: 'spring',
                    bounce: 0,
                    duration: 0.8
                  }} data-id="element-34" />
                    </div>
                  </div>
                </div>
              </motion.div>;
        })}
        </AnimatePresence>

        {goals.length === 0 && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="text-center py-12 px-4 border-2 border-dashed border-gray-200 rounded-2xl" data-id="element-35">
            <Target size={48} className="mx-auto text-gray-300 mb-4" data-id="element-36" />
            <h3 className="text-lg font-medium text-gray-700 mb-1" data-id="element-37">
              No goals in sight
            </h3>
            <p className="text-gray-500" data-id="element-38">
              Set a new target above to get started.
            </p>
          </motion.div>}
      </div>
    </div>;
}